import React, { FC, Fragment, useEffect, useRef } from 'react';
import { formatDate } from 'utils/format';
import { OrderRejectStatus, OrderStatus } from 'models/order';
import { Alert, Box, Typography } from '@mui/material';
import OrderStatusChip from '../../../orders/shared/order-status-chip.component';
import { useQuery } from '@apollo/client';
import { getStatusLogsTemplate } from 'apollo/templates/order';
import { OrderEvent } from 'models/order-event';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { useTranslation } from 'react-i18next';

type OrderStatusLogProps = {
    orderId: string;
    status: OrderStatus;
    rejectStatus?: OrderRejectStatus;
};

const OrderStatusLog: FC<OrderStatusLogProps> = ({ orderId, status, rejectStatus }: OrderStatusLogProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading, refetch } = useQuery(getStatusLogsTemplate, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        variables: {
            orderId: orderId,
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const statusLogs: OrderEvent[] = data?.statusLogs ?? [];

    // DD-3679: the order is refreshed in place (a mutation result or an ORDER_CHANGED refresh),
    // but the history is its own query. A status change writes a new entry, so re-read it then.
    // Another order needs nothing here: the query re-runs on its new orderId by itself.
    const statusKey = `${status}:${rejectStatus ?? ''}`;
    const loadedStatus = useRef({ orderId, statusKey });
    useEffect(() => {
        const isSameOrder = loadedStatus.current.orderId === orderId;
        const hasStatusChanged = loadedStatus.current.statusKey !== statusKey;
        loadedStatus.current = { orderId, statusKey };
        if (isSameOrder && hasStatusChanged) {
            refetch();
        }
    }, [orderId, statusKey]);

    // Only the first load shows the loader; a refresh keeps the current list on screen.
    if (loading && !data) {
        return <Loader />;
    }

    return statusLogs.length ? (
        <Fragment>
            {statusLogs.map((data, index) => (
                <Box key={index} mb={1}>
                    <Box>
                        <Typography variant="subtitle2">
                            <Box display="flex" alignItems="center">
                                <Box mr={0.5}>{index + 1}</Box>
                                <OrderStatusChip status={data.message as OrderStatus} />
                            </Box>
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            {formatDate(new Date(data.date), 'datetime')}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            {data.user?.fullName || 'Denthub'}
                        </Typography>
                        {data.manualSelectedDate && data.message === OrderStatus.ACCEPTED_BY_LAB ? (
                            <Typography variant="body2" color="text.secondary">
                                {translate('orders:SELECTED_PREVIOUS_DATE')}{' '}
                                {formatDate(new Date(data.manualSelectedDate), 'date')}
                            </Typography>
                        ) : undefined}
                        {data.manualSelectedDate && data.message === OrderStatus.SENT_BY_LAB ? (
                            <Typography variant="body2" color="text.secondary">
                                {translate('orders:SENT_DATE')} {formatDate(new Date(data.manualSelectedDate), 'date')}
                            </Typography>
                        ) : undefined}
                    </Box>
                </Box>
            ))}
        </Fragment>
    ) : (
        <Alert severity="info">{translate('orders:NO_STATUS_LOGS')}</Alert>
    );
};

export default OrderStatusLog;
